import { buildAiAssistantObjectLink } from "@/components/ai-assistant/ai-assistant-object-link.ts";
import { Button, ButtonLink, Stack, Text } from "@skailer-com/uikit";
import { DynamicToolUIPart, getToolName, ToolUIPart } from "ai";
import { Fragment, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

export type AiAssistantToolPart = ToolUIPart | DynamicToolUIPart;

export type AiAssistantApprovalResponse = (response: { id: string; approved: boolean }) => void;

export interface AiAssistantApprovalCardRow {
  label: string;
  value: ReactNode;
}

interface AiAssistantApprovalCardProps {
  part: AiAssistantToolPart;
  title: string;
  subtitle?: string;
  rows: AiAssistantApprovalCardRow[];
  onApprovalResponse: AiAssistantApprovalResponse;
}

export function AiAssistantApprovalCard({
  part,
  title,
  subtitle,
  rows,
  onApprovalResponse,
}: AiAssistantApprovalCardProps) {
  const { t } = useTranslation();
  const [isAnswered, setIsAnswered] = useState(false);

  const approval = part.approval;
  const isCancelled = part.state === "output-denied" || approval?.approved === false;
  const canAnswer = part.state === "approval-requested" && !isAnswered;

  const respond = (approved: boolean) => {
    if (!approval || !canAnswer) return;
    setIsAnswered(true);
    onApprovalResponse({ id: approval.id, approved });
  };

  const result =
    part.state === "output-available"
      ? { text: t("ai:assistant_action_done"), className: "text-positive" }
      : part.state === "output-error"
        ? { text: t("ai:assistant_action_failed"), className: "text-danger" }
        : isCancelled
          ? { text: t("ai:assistant_action_cancelled"), className: "text-secondary" }
          : undefined;

  const objectLink =
    part.state === "output-available"
      ? buildAiAssistantObjectLink(
          getToolName(part),
          (part.input ?? {}) as Record<string, unknown>,
          part.output
        )
      : undefined;

  return (
    <Stack gap={"sm"}>
      <Stack gap={"sm"} className={"border-l-2 border-dark/20 pl-4"}>
        <Stack gap={"xs"}>
          <Text size={"sm"} className={"font-mono font-medium uppercase tracking-wider"}>
            {title}
          </Text>
          {subtitle && (
            <Text size={"sm"} className={"text-secondary"}>
              {subtitle}
            </Text>
          )}
        </Stack>
        {rows.length > 0 && (
          <dl className={"grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm"}>
            {rows.map((row, index) => (
              <Fragment key={index}>
                <dt className={"text-secondary"}>{row.label}</dt>
                <dd className={"break-words"}>{row.value}</dd>
              </Fragment>
            ))}
          </dl>
        )}
      </Stack>
      {!result && (
        <Stack direction={"horizontal"} gap={"sm"}>
          <Button
            size={"sm"}
            disabled={!canAnswer}
            isLoading={approval?.approved === true}
            onClick={() => respond(true)}
          >
            {t("common:confirm")}
          </Button>
          <Button
            size={"sm"}
            variant={"secondary"}
            disabled={!canAnswer}
            onClick={() => respond(false)}
          >
            {t("common:cancel")}
          </Button>
        </Stack>
      )}
      <Stack direction={"horizontal"} items={"center"} gap={"sm"}>
        <div role={"status"}>
          {result && (
            <Text size={"sm"} className={result.className}>
              {result.text}
            </Text>
          )}
        </div>
        {objectLink && (
          <ButtonLink size={"sm"} variant={"secondary"} href={objectLink}>
            {t("ai:assistant_action_open")}
          </ButtonLink>
        )}
      </Stack>
    </Stack>
  );
}
